import {
  alpha,
  Divider,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ElbowIcon, WarningIcon } from "~/icons";
import { ImpactUpdated } from "~/components/impact/Updated/impact-updated";
import { useMetrics } from "~/hooks";
import { useScope } from "~/hooks/useScope";
import { getColor } from "~/lib/colors";
import { Metrics } from "~/lib/arrow";
import { Link as RouterLink } from "react-router-dom";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { MTTRDate } from "~/pages/space/home-dashboard/MTTRSla/components/MTTRDate";
import { ScoreRating } from "~/operations";
import { metricRatingToRiskRating } from "./hooks/useSlasDashboard";

export interface SlasRatingFiltersProps {
  activeRatings?: ScoreRating[];
  slasDashUrl: URL;
}

export function SlasRatingFilters({
  activeRatings = [],
  slasDashUrl,
}: SlasRatingFiltersProps) {
  const { isFeatureEnabled } = useFeatureFlags();
  const isLandscape = useMediaQuery("(orientation: landscape)");
  const theme = useTheme();
  const { activeScope } = useScope();

  const { mttrSla } = useMetrics({
    entityMrn: activeScope?.mrn || "",
    metricMrns: [Metrics.MTTRSla],
  });

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {mttrSla.map((sla) => {
        const { rating, mttr, nearDate, pastDate, slaDays } = sla;
        const riskRating = metricRatingToRiskRating(rating);
        const color = getColor(theme, rating);
        const slasPastDue = Boolean(pastDate);
        const isActive = activeRatings.includes(riskRating);
        if (isActive) {
          slasDashUrl.searchParams.delete("riskRating");
        } else {
          slasDashUrl.searchParams.set("riskRating", riskRating);
        }

        return (
          <Grid
            key={rating}
            component={RouterLink}
            to={slasDashUrl.href}
            size={{ xs: 12, sm: isLandscape ? 3 : 6, lg: 3 }}
            sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: "background.paper",
              transition: (theme) =>
                theme.transitions.create(["opacity", "box-shadow"]),

              ...(isActive
                ? {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                    boxShadow: (theme) =>
                      `inset 0 0 0 1px ${theme.palette.primary.main}`,
                  }
                : {}),

              ...(!isActive
                ? {
                    "&:hover": {
                      opacity: 1,
                      boxShadow: (theme) =>
                        `inset 0 0 0 1px ${theme.palette.divider}`,
                    },

                    ...(activeRatings.length > 0
                      ? {
                          opacity: 0.5,
                        }
                      : {}),
                  }
                : {}),
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <ImpactUpdated
                impact={{
                  rating: rating,
                }}
                hideTooltip
                options={{ fontSize: 12, fontWeight: 700 }}
              />
              <MTTRDate mttr={mttr} color={color} />
            </Stack>

            <Divider sx={{ my: 1 }} />

            <Stack direction="column" rowGap={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: "12px",
                    color: "text.secondary",
                  }}
                >
                  SLA
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: "12px",
                    color: theme.palette.text.primary,
                  }}
                >
                  {slaDays}d
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" gap={0.5}>
                <Stack direction="row" gap={0.5} sx={{ pl: 0.5 }}>
                  <ElbowIcon sx={{ fontSize: 8 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: "12px",
                      color: "text.secondary",
                    }}
                  >
                    Findings nearing SLA date
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: "12px",
                    color: theme.palette.text.primary,
                  }}
                >
                  {nearDate}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" gap={0.5}>
                <Stack direction="row" gap={0.5} sx={{ pl: 0.5 }}>
                  <ElbowIcon sx={{ fontSize: 8 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: "12px",
                      color: "text.secondary",
                    }}
                  >
                    Findings past SLA date
                  </Typography>
                </Stack>
                <Stack
                  position="relative"
                  direction="row"
                  gap={0.5}
                  alignItems="center"
                >
                  {slasPastDue && (
                    <WarningIcon
                      sx={{
                        position: "absolute",
                        left: -16,
                        top: -0.5,
                        fontSize: 14,
                        color: "warning.main",
                      }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: "12px",
                      color: slasPastDue
                        ? theme.palette.text.primary
                        : theme.palette.text.disabled,
                    }}
                  >
                    {pastDate}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Grid>
        );
      })}
    </Grid>
  );
}
